import { createContext, type ReactNode, useContext, useMemo, useState } from 'react'
import { DEFAULT_LANGUAGE, getStrings, type Language, type Strings } from './strings'

interface LanguageContextValue {
  readonly language: Language
  readonly strings: Strings
  setLanguage(language: Language): void
}

const LanguageContext = createContext<LanguageContextValue>({
  language: DEFAULT_LANGUAGE,
  strings: getStrings(DEFAULT_LANGUAGE),
  setLanguage: () => undefined,
})

export const LanguageProvider = ({
  children,
  initialLanguage = DEFAULT_LANGUAGE,
}: {
  children: ReactNode
  initialLanguage?: Language
}) => {
  const [language, setLanguage] = useState<Language>(initialLanguage)

  const value = useMemo<LanguageContextValue>(
    () => ({ language, strings: getStrings(language), setLanguage }),
    [language],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

/** All copy comes from here. Never write a literal string in a component. */
export const useStrings = (): Strings => useContext(LanguageContext).strings

export const useLanguage = (): Pick<LanguageContextValue, 'language' | 'setLanguage'> => {
  const { language, setLanguage } = useContext(LanguageContext)
  return { language, setLanguage }
}
